import { SOUND_NOTIFICATION_TYPES } from 'enum/helpers';

import diceRollSound from '../../sounds/dice_roll.mp3';
import kenoSound from '../../sounds/token_keno_action.mp3';
import kenoEndSound from '../../sounds/token_keno_end.mp3';
import privateMessageSound from '../../sounds/private_message.mp3';
import privateShowRequestSound from '../../sounds/private_show_request.mp3';
import tippingGameStart from '../../sounds/tipping_game_start.mp3';
import wheelSound from '../../sounds/spin_the_wheel.mp3';
import wheelEndSound from '../../sounds/spin_the_wheel_win.mp3';


const sounds = {
  [SOUND_NOTIFICATION_TYPES.GAME]: tippingGameStart,
  [SOUND_NOTIFICATION_TYPES.PRIVATE_MESSAGE_SOUND]: privateMessageSound,
  [SOUND_NOTIFICATION_TYPES.PRIVATE_SHOW_REQUEST_SOUND]: privateShowRequestSound,
  [SOUND_NOTIFICATION_TYPES.ROLL_THE_DICE]: diceRollSound,
  [SOUND_NOTIFICATION_TYPES.SPIN_THE_WHEEL]: wheelSound,
  [SOUND_NOTIFICATION_TYPES.SPIN_THE_WHEEL_END]: wheelEndSound,
  [SOUND_NOTIFICATION_TYPES.TOKEN_KENO]: kenoSound,
  [SOUND_NOTIFICATION_TYPES.TOKEN_KENO_END]: kenoEndSound,
};

/**
 * Array of sound types that play instantly and pause
 * incoming sound types until it's finished then countinue
 * playing rest of the sounds if they are available
 *
 * This array could be modified to support more sound types like this
 */
const instantQueueSounds = [
  SOUND_NOTIFICATION_TYPES.ROLL_THE_DICE,
  SOUND_NOTIFICATION_TYPES.SPIN_THE_WHEEL,
  SOUND_NOTIFICATION_TYPES.SPIN_THE_WHEEL_END,
  SOUND_NOTIFICATION_TYPES.TOKEN_KENO,
  SOUND_NOTIFICATION_TYPES.TOKEN_KENO_END,
];

const status = {
  stopped: 'stopped',
  playing: 'playing',
};

/**
 * TippingSoundService that provides abstraction over tipping sounds
 * @description
 * Service contains 2 main queue pipelines that play sound in sequence one after another
 */
class TippingSoundService {
  /**
   * Track status of each queue
   */
  status = {
    queue: status.stopped,
    instantQueue: status.stopped,
  };

  /**
   * this.queue push all sounds that are not in instantQueueSounds array definition
   * This means that queue has lower priority over instantQueue.
   */
  queue = [];

  /**
   * this.instantQueue has top priority and it takes in consideration instantQueueSounds definition
   * Any type of this sound will run this instantQueue sounds that means
   * if queue playing some sounds instantQueue will pause it and continue after job is done
   */
  instantQueue = [];

  /**
   * @method play - Play sound method
   * @param {string} sound - one of SOUND_NOTIFICATION_TYPES or sound file itself
   * @param {number} volume - range value [0-1]
   * @throws Error if sound type is wrong
   */
  play = (sound, volume = 0) => {
    const audioUrl = sounds[sound] || sound;
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.volume = volume;
      if (instantQueueSounds.includes(sound)) {
        this._startQueue('instantQueue', audio);
      } else {
        this._startQueue('queue', audio);
      }
    } else {
      throw Error(`Sound type ${sound} doesn't exist!`);
    }
  };

  /**
   * Everything below are private helper methods for integration
   */

  _setEndedEvents = (queueType, audio) => {
    if (queueType === 'queue') {
      audio.onended = this._onEndedQueuePlay;
    }

    if (queueType === 'instantQueue') {
      audio.onended = this._onEndedInstantPlay;
    }
  };

  _setEndedInstantEvents = (audio) => {
    audio.onended = this._onEndedInstantPlay;
  };

  _startQueue = (queueType, audio) => {
    this._setStatus(queueType, status.playing);
    this._setEndedEvents(queueType, audio);
    this._addAudioToQueue(queueType, audio);

    if (queueType === 'instantQueue') {
      if (this.status.queue === status.playing) {
        this._pauseQueue();
      }
      this._playQueue('instantQueue');
    }

    if (queueType === 'queue') {
      if (this.status.instantQueue === status.stopped) {
        this._playQueue('queue');
      }
    }
  };

  _pauseQueue = () => {
    const currentIndex = this._getCurrentIndex('queue');
    this.queue[currentIndex].pause();
  };

  _onEndedInstantPlay = () => {
    const currentIndex = this._getCurrentIndex('instantQueue');
    this._removePlayedAudio('instantQueue', currentIndex);

    if (this.instantQueue[currentIndex]) {
      this._playQueue('instantQueue');
    } else {
      this._setStatus('instantQueue', status.stopped);
      this._playQueue('queue');
    }
  };

  _onEndedQueuePlay = () => {
    const currentIndex = this._getCurrentIndex('queue');
    this._removePlayedAudio('queue', currentIndex);

    if (this.queue[currentIndex]) {
      this._playQueue('queue');
    } else {
      this._setStatus('queue', status.stopped);
    }
  };

  _getCurrentIndex = (propName) => (this[propName].indexOf(this) + 1) % this[propName].length;

  _addAudioToQueue = (queueType, audio) => {
    this[queueType].push(audio);
  };

  _removePlayedAudio = (propName, index) => {
    const audio = this[propName][index];
    /**
     * Before removing Audio constructor make sure that
     * audio is paused and currentTime set to 0
     * this way object become subject to garbage collection
     */
    audio.pause();
    audio.currentTime = 0;
    this[propName].shift();
  };

  _playQueue = (propName) => {
    if (this[propName].length > 0) {
      const playPromise = this[propName][0].play();
      if (playPromise) {
        playPromise.catch((e) => {
          if (propName === 'queue') {
            this._onEndedQueuePlay();
          }
          if (propName === 'instantQueue') {
            this._onEndedInstantPlay();
          }
          // eslint-disable-next-line no-console -- TODO: Error handling
          console.error('Error playing tipping sound', e);
        });
      }
    }
  };

  _setStatus = (queueType, newStatus) => (this.status[queueType] = newStatus);
}

export default new TippingSoundService();
